import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

export const getAdminToken = () => {
  try {
    return localStorage.getItem('adminToken');
  } catch {
    return null;
  }
};

/** Client-side expiry check for JWTs (the server still verifies). Malformed/missing tokens count as expired. */
export const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const [, payload] = token.split('.');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp !== 'number' || exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

/**
 * Pick which token the Course Studio should call the API with.
 * - preferAdmin (inside /admin/*): a valid admin token first so every course
 *   loads, falling back to the teacher token when the admin one is missing/expired.
 * - legacy: the teacher token first, then admin.
 */
export const selectStudioToken = ({ user, token, adminToken, preferAdmin = false }) => {
  const teacherToken = user?.role === 'teacher' && token ? token : null;
  if (!preferAdmin) return teacherToken || adminToken || token;
  const validAdmin = adminToken && !isTokenExpired(adminToken) ? adminToken : null;
  return validAdmin || teacherToken || adminToken || token;
};

/**
 * Auth headers for Academy pages used by students, teachers *and* admins.
 * - player: the signed-in Academy account first (students track progress),
 *   then the admin console token for previews.
 * - studio: a signed-in teacher first, then the admin console token; with
 *   { preferAdmin: true } (used inside the admin panel) a valid admin token
 *   wins so admins always see every course.
 */
export default function useStaffHeaders(mode = 'player', { preferAdmin = false } = {}) {
  const { token, user } = useAuth();
  const adminToken = getAdminToken();
  let activeToken;
  if (mode === 'studio') {
    activeToken = selectStudioToken({ user, token, adminToken, preferAdmin });
  } else {
    activeToken = token || adminToken;
  }
  const getHeaders = useCallback(
    () => (activeToken ? { Authorization: `Bearer ${activeToken}` } : {}),
    [activeToken]
  );
  return { getHeaders, hasToken: Boolean(activeToken), viaAdmin: activeToken === adminToken && Boolean(adminToken) };
}
